import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import BrandLogo from "@/components/feature/BrandLogo";

export default function Footer() {
  const { brand, footerLinks, socialLinks, legalLinks } = useSiteSettings();

  return (
    <footer className="bg-primary-900 text-background-100">
      <div className="w-full px-4 md:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-4">
            <div className="mb-5">
              <BrandLogo imgClassName="h-12 md:h-14" />
            </div>
            <p className="text-sm leading-relaxed text-background-200 max-w-sm">
              Connecting people with farms, farmers, village life and local food — creating
              meaningful experiences that bring us closer to where our food comes from.
            </p>
            <p className="font-heading italic text-accent-300 mt-5 text-lg">
              {brand.tagline}
            </p>
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-800 text-background-100 hover:bg-accent-500 hover:text-accent-950 transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {footerLinks.map((col) => (
            <div key={col.heading} className="lg:col-span-2">
              <h4 className="text-sm font-semibold text-background-50 mb-4 uppercase tracking-wider">
                {col.heading}
              </h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-background-200 hover:text-accent-300 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-background-50 mb-4 uppercase tracking-wider">
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-background-200">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-accent-300"></i>
                </span>
                <span>{brand.region}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-line text-accent-300"></i>
                </span>
                <a href={`mailto:${brand.email}`} className="hover:text-accent-300 transition-colors">
                  {brand.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-phone-line text-accent-300"></i>
                </span>
                <a href={`tel:${brand.phone}`} className="hover:text-accent-300 transition-colors">
                  {brand.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-14 pt-6 border-t border-primary-800 text-xs text-background-300">
          <p>© {new Date().getFullYear()} {brand.fullName}. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {legalLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="hover:text-accent-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 hover:text-accent-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shield-user-line"></i>
              Team login
            </Link>
          </div>
          <p className="flex items-center gap-1">
            Made with <i className="ri-heart-fill text-accent-400"></i> for farmers and the land.
          </p>
        </div>
      </div>
    </footer>
  );
}